/**
 * TodoSortFilterMenu - Popover menu for sort and filter options.
 */

import React, { useState, useRef } from 'react';
import { Pressable, StyleSheet, Text, View } from 'react-native';
import Popover from 'react-native-popover-view';

import { IconSymbol } from '@/components/ui/icon-symbol';
import { Colors } from '@/constants/theme';
import { Spacing, BorderRadius } from '@/constants/spacing';
import { useColorScheme } from '@/hooks/use-color-scheme';
import type { TodoSortOption, TodoFilterOption } from '@/types/todo-preferences';

type TodoSortFilterMenuProps = {
  sort: TodoSortOption;
  filter: TodoFilterOption;
  onSortChange: (sort: TodoSortOption) => void;
  onFilterChange: (filter: TodoFilterOption) => void;
  onOpenManageLists?: () => void;
};

type SortIconName = 'arrow.up.arrow.down' | 'textformat.abc' | 'clock' | 'clock.arrow.circlepath' | 'flag.fill' | 'flag';

type SortOptionConfig = {
  value: TodoSortOption;
  label: string;
  icon: SortIconName;
};

type FilterOptionConfig = {
  value: TodoFilterOption;
  label: string;
};

const SORT_OPTIONS: SortOptionConfig[] = [
  { value: 'manual', label: 'Manual', icon: 'arrow.up.arrow.down' },
  { value: 'alpha-asc', label: 'A to Z', icon: 'textformat.abc' },
  { value: 'alpha-desc', label: 'Z to A', icon: 'textformat.abc' },
  { value: 'newest', label: 'Newest First', icon: 'clock' },
  { value: 'oldest', label: 'Oldest First', icon: 'clock.arrow.circlepath' },
  { value: 'priority-first', label: 'Priority First', icon: 'flag.fill' },
  { value: 'normal-first', label: 'Normal First', icon: 'flag' },
];

const FILTER_OPTIONS: FilterOptionConfig[] = [
  { value: 'all', label: 'All Tasks' },
  { value: 'priority-only', label: 'Priority Only' },
  { value: 'normal-only', label: 'Normal Only' },
];

const ANIMATION_DURATION_MS = 80;
const PRIORITY_RED = '#E53935';

export function TodoSortFilterMenu({
  sort,
  filter,
  onSortChange,
  onFilterChange,
  onOpenManageLists,
}: TodoSortFilterMenuProps) {
  const colorScheme = useColorScheme();
  const palette = Colors[colorScheme ?? 'light'];
  const [isVisible, setIsVisible] = useState(false);
   
  const buttonRef = useRef<any>(null);

  const currentSortOption = SORT_OPTIONS.find(opt => opt.value === sort);
  const triggerIcon = currentSortOption?.icon ?? 'arrow.up.arrow.down';

  const handleSortSelect = (value: TodoSortOption) => {
    onSortChange(value);
  };

  const handleFilterSelect = (value: TodoFilterOption) => {
    onFilterChange(value);
  };

  return (
    <>
      <View ref={buttonRef} collapsable={false}>
        <Pressable
          onPress={() => setIsVisible(true)}
          style={styles.triggerButton}
          hitSlop={8}
        >
          <IconSymbol
            name={triggerIcon}
            size={20}
            color={palette.icon}
          />
        </Pressable>
      </View>

      <Popover
        isVisible={isVisible}
        onRequestClose={() => setIsVisible(false)}
        from={buttonRef}
        popoverStyle={[styles.popover, { backgroundColor: palette.cardBackground }]}
        arrowSize={{ width: 12, height: 8 }}
        backgroundStyle={styles.backdrop}
        animationConfig={{ duration: ANIMATION_DURATION_MS }}
      >
        <View style={styles.menuContent}>
          <Text style={[styles.sectionTitle, { color: palette.textMuted }]}>
            Filter
          </Text>
          {FILTER_OPTIONS.map(option => (
            <MenuOptionRow
              key={option.value}
              label={option.label}
              isSelected={filter === option.value}
              onPress={() => handleFilterSelect(option.value)}
              palette={palette}
            />
          ))}

          <View style={[styles.divider, { backgroundColor: palette.border }]} />

          <Text style={[styles.sectionTitle, { color: palette.textMuted }]}>
            Sort
          </Text>
          {SORT_OPTIONS.map(option => (
            <MenuOptionRow
              key={option.value}
              label={option.label}
              icon={option.icon}
              isSelected={sort === option.value}
              onPress={() => handleSortSelect(option.value)}
              palette={palette}
            />
          ))}

          {onOpenManageLists && (
            <>
              <View style={[styles.divider, { backgroundColor: palette.border }]} />
              <MenuOptionRow
                label="Manage lists…"
                isSelected={false}
                onPress={() => {
                  setIsVisible(false);
                  onOpenManageLists();
                }}
                palette={palette}
              />
            </>
          )}
        </View>
      </Popover>
    </>
  );
}

type MenuOptionRowProps = {
  label: string;
  icon?: SortIconName;
  isSelected: boolean;
  onPress: () => void;
  palette: (typeof Colors)['light'];
};

function MenuOptionRow({ label, icon, isSelected, onPress, palette }: MenuOptionRowProps) {
  const isPriorityIcon = icon === 'flag.fill';
  const iconColor = isSelected
    ? (isPriorityIcon ? PRIORITY_RED : palette.primary)
    : palette.textMuted;

  return (
    <Pressable
      onPress={onPress}
      style={({ pressed }) => [
        styles.optionRow,
        pressed && { backgroundColor: palette.borderMuted },
      ]}
    >
      <View style={styles.optionLabelContainer}>
        {icon && (
          <IconSymbol
            name={icon}
            size={16}
            color={iconColor}
            style={styles.optionIcon}
          />
        )}
        <Text
          style={[
            styles.optionLabel,
            { color: palette.text },
            isSelected && styles.optionLabelSelected,
          ]}
        >
          {label}
        </Text>
      </View>
      {isSelected && (
        <IconSymbol name="checkmark" size={16} color={palette.primary} />
      )}
    </Pressable>
  );
}

const styles = StyleSheet.create({
  triggerButton: {
    padding: Spacing.xs,
    borderRadius: BorderRadius.sm,
    marginTop: -3,
  },
  popover: {
    borderRadius: BorderRadius.md,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 4 },
    shadowOpacity: 0.15,
    shadowRadius: 12,
    elevation: 8,
  },
  backdrop: {
    backgroundColor: 'rgba(0, 0, 0, 0.3)',
  },
  menuContent: {
    minWidth: 180,
    paddingVertical: Spacing.sm,
  },
  sectionTitle: {
    fontSize: 12,
    fontWeight: '600',
    textTransform: 'uppercase',
    letterSpacing: 0.5,
    paddingHorizontal: Spacing.md,
    paddingVertical: Spacing.xs,
  },
  optionRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: Spacing.md,
    paddingVertical: Spacing.sm,
  },
  optionLabelContainer: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  optionIcon: {
    marginRight: Spacing.sm,
  },
  optionLabel: {
    fontSize: 15,
  },
  optionLabelSelected: {
    fontWeight: '600',
  },
  divider: {
    height: 1,
    marginVertical: Spacing.sm,
    marginHorizontal: Spacing.md,
  },
});
